import React, { useState } from "react";

// React Router Dom
import { Link, useNavigate } from "react-router-dom";

// Hooks
import useSnackbarNotification from "../hooks/useSnackbarNotification";

// React Icons
import { FaEye, FaEyeSlash } from "react-icons/fa";

// Contexts
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { supabase } from "../supabase";

const LoginPage = () => {
    const navigate = useNavigate();

    const { signIn, user } = useAuth();
    const { profileInfo, migrateFromMetadata, getProfileInfo } = useProfileInfo();
    const { showError, showSuccess } = useSnackbarNotification();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [emailError, setEmailError] = useState(false);

    const determineCredentialErrorMessage = async (attemptedEmail) => {
        if (!attemptedEmail) return null;

        try {
            const { data, error } = await supabase
                .from("users_profiles")
                .select("profileid")
                .ilike("email", attemptedEmail)
                .maybeSingle();

            // Ignore "no rows" error (PGRST116). Treat anything else as unexpected.
            if (error && error.code !== "PGRST116") {
                console.warn("Credential lookup failed:", error);
                return null;
            }

            if (!data) {
                return {
                    message:
                        "We couldn't find an account with this email address. Please check the spelling or sign up.",
                    field: "email",
                };
            }

            return {
                message:
                    "Incorrect password. If you've forgotten it, click 'Forgot Password' to reset it.",
                field: "password",
            };
        } catch (lookupError) {
            console.warn("Credential lookup threw an exception:", lookupError);
            return null;
        }
    };

    const getFriendlyErrorMessage = (err) => {
        if (!err) {
            return "Something went wrong. Please try again.";
        }

        const normalizedMessage = err.message?.toLowerCase() || "";
        const status = err.status || err.statusCode;

        // Invalid credentials - wrong email or password
        if (
            normalizedMessage.includes("invalid login credentials") ||
            normalizedMessage.includes("invalid email or password") ||
            normalizedMessage.includes("invalid credentials") ||
            status === 400
        ) {
            return "Incorrect email or password. Please check your credentials and try again.";
        }

        // Email not confirmed / needs verification
        if (
            normalizedMessage.includes("email not confirmed") ||
            normalizedMessage.includes("email_not_confirmed") ||
            normalizedMessage.includes("confirmation required") ||
            normalizedMessage.includes("verify email") ||
            normalizedMessage.includes("email verification") ||
            normalizedMessage.includes("email needs to be verified") ||
            normalizedMessage.includes("email must be verified") ||
            normalizedMessage.includes("unverified email") ||
            normalizedMessage.includes("email is not verified") ||
            normalizedMessage.includes("please verify your email") ||
            normalizedMessage.includes("verify your email address") ||
            normalizedMessage.includes("email verification required") ||
            normalizedMessage.includes("user must verify") ||
            normalizedMessage.includes("account not verified")
        ) {
            return "Please verify your email address before logging in. Check your inbox (and spam folder) for the verification link. If you didn't receive it, you can request a new one.";
        }

        // User not found / account doesn't exist
        if (
            normalizedMessage.includes("user not found") ||
            normalizedMessage.includes("no user found") ||
            normalizedMessage.includes("user_not_found")
        ) {
            return "No account found with this email address. Please sign up or check your email.";
        }

        // Rate limiting - too many requests
        if (
            status === 429 ||
            normalizedMessage.includes("too many requests") ||
            normalizedMessage.includes("rate limit") ||
            normalizedMessage.includes("too_many_requests")
        ) {
            return "Too many login attempts. Please wait a few minutes before trying again.";
        }

        // Network/connection errors
        if (
            normalizedMessage.includes("network") ||
            normalizedMessage.includes("fetch") ||
            normalizedMessage.includes("connection") ||
            normalizedMessage.includes("timeout") ||
            normalizedMessage.includes("failed to fetch")
        ) {
            return "Unable to connect to the server. Please check your internet connection and try again.";
        }

        // Email rate limit (sending too many emails)
        if (
            normalizedMessage.includes("email rate limit") ||
            normalizedMessage.includes("email_rate_limit_exceeded")
        ) {
            return "Too many emails sent. Please wait before requesting another email.";
        }

        // Account disabled or suspended
        if (
            normalizedMessage.includes("disabled") ||
            normalizedMessage.includes("suspended") ||
            normalizedMessage.includes("banned")
        ) {
            return "This account has been disabled. Please contact support for assistance.";
        }

        // Server errors (5xx)
        if (status >= 500) {
            return "Server error. Please try again in a few moments.";
        }

        // Return the original error message if it's user-friendly, otherwise generic message
        if (err.message && err.message.length < 100) {
            return err.message;
        }

        return "Failed to log in. Please check your email and password, or try again later.";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setPasswordError(false);
        setEmailError(false);
        setLoading(true);

        // Client-side validation
        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();

        if (!trimmedEmail) {
            setError("Please enter your email address.");
            setEmailError(true);
            setLoading(false);
            return;
        }

        if (!trimmedPassword) {
            setError("Please enter your password.");
            setPasswordError(true);
            setLoading(false);
            return;
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            setError("Please enter a valid email address.");
            setEmailError(true);
            setLoading(false);
            return;
        }

        // Password length validation
        if (trimmedPassword.length < 6) {
            setError("Password must be at least 6 characters long.");
            setPasswordError(true);
            setLoading(false);
            return;
        }

        try {
            const { data: signInData, error: signInError } = await signIn(trimmedEmail, trimmedPassword);
            

            if (signInError) {
                throw signInError;
            }
            
            const signedInUser = signInData?.user;
            
            // Fetch profile directly from database using the signed-in user ID (before any navigation)
            let { data: currentProfileInfo, error: profileError } = await supabase
                .from("users_profiles")
                .select("*")
                .eq("profileid", signedInUser?.id)
                .single();
            
            // If still no profile, try to migrate from metadata
            if (!currentProfileInfo && profileError) {
                await migrateFromMetadata();
                // Fetch again after migration
                await new Promise(resolve => setTimeout(resolve, 500));
                const { data: migratedProfile } = await supabase
                    .from("users_profiles")
                    .select("*")
                    .eq("profileid", signedInUser?.id)
                    .single();
                currentProfileInfo = migratedProfile;
            }
            
            // SIMPLE LOGIC: Check if onboarding is completed
            // Database uses lowercase 'onboardingcompleted'
            const isOnboardingCompleted = currentProfileInfo?.onboardingcompleted === true;
            
            // Navigate IMMEDIATELY to prevent showing "/" route from AuthContext
            // The auth state listener will update the user context automatically
            if (isOnboardingCompleted) {
                // Onboarding completed - go to dashboard
                const dashboardPath = currentProfileInfo?.role === 'Employer' 
                    ? "/employer/dashboard" 
                    : "/employee/dashboard";
                // Navigate immediately with replace to override any pending navigations
                navigate(dashboardPath, { replace: true });
                showSuccess("Login successful");
                setLoading(false);
                return;
            }
            
            // Onboarding NOT completed - go to role selection
            navigate("/role-selection", { replace: true });
            setLoading(false);
        } catch (error) {
            let friendlyMessage = getFriendlyErrorMessage(error);

            // Set field-specific error states for better UX
            const normalizedMessage = error.message?.toLowerCase() || "";
            const isInvalidCredentialsError =
                normalizedMessage.includes("invalid login credentials") ||
                normalizedMessage.includes("invalid email or password") ||
                normalizedMessage.includes("invalid credentials");

            if (isInvalidCredentialsError) {
                const credentialDetails = await determineCredentialErrorMessage(trimmedEmail);
                if (credentialDetails) {
                    friendlyMessage = credentialDetails.message;
                    if (credentialDetails.field === "email") {
                        setEmailError(true);
                    } else if (credentialDetails.field === "password") {
                        setPasswordError(true);
                        setPassword("");
                    }
                } else {
                    // Could be either email or password wrong, highlight both
                    setPasswordError(true);
                    setEmailError(true);
                    setPassword("");
                }
            } else if (normalizedMessage.includes("email")) {
                setEmailError(true);
            } else if (normalizedMessage.includes("password")) {
                setPasswordError(true);
                setPassword("");
            }

            showError(friendlyMessage);
            setError(friendlyMessage);
        }
        setLoading(false);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden md:flex md:w-1/2 bg-gray-50  flex-col space-y-6 overflow-auto">
                <img
                    src="/login.jpg"
                    alt="Login"
                    className="w-full h-full object-cover"
                />
            </div>

            <div className="w-full md:w-1/2 bg-gray-50 flex flex-col justify-center px-8 md:px-20">
                <div className="max-w-md w-full mx-auto">
                    <div
                        className="logo cursor-pointer justify-center mb-8"
                        onClick={() => navigate("/")}
                    >
                        Doctors' Jobs <span className="logo-badge">Beta</span>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                        Welcome Back!
                    </h2>

                    {error && (
                        <div className="mb-6 rounded-md bg-red-50 p-4 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Email Address *
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError(false);
                                    setError("");
                                }}
                                className={`appearance-none block w-full px-3 py-2 border ${
                                    emailError ? "border-red-500" : "border-gray-300"
                                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="password"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Password *
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError(false);
                                        setError("");
                                    }}
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        passwordError ? "border-red-500" : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword ? (
                                        <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <FaEye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) =>
                                        setRememberMe(e.target.checked)
                                    }
                                    className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                                />
                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-base text-gray-900"
                                >
                                    Remember me
                                </label>
                            </div>

                            <div className="text-base">
                                <a
                                    href="/forgot-password"
                                    className="font-medium text-black hover:text-black hover:underline"
                                >
                                    Forgot Password?
                                </a>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                            >
                                {loading ? "Signing in..." : "Sign in"}
                            </button>
                        </div>
                    </form>

                    <p className="mt-6 text-center text-base text-gray-600">
                        Don't have an account?{" "}
                        <Link
                            to="/signup"
                            className="font-medium text-black hover:text-gray-700"
                        >
                            Sign Up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
